import { apiClient } from './api.client';

const base = '/camera-security/event-actions';
export type OutputType = 'builtin' | 'ip_speaker' | 'nvr';
export interface AudioSettings {
  enabled: boolean; output_type: OutputType; device_id: string | null; channel_id: number;
  volume: number; voice: string; entry_direction: 'A_TO_B' | 'B_TO_A'; location_id?: number;
}
export interface OutputDevice {
  id: string; name: string; kind: OutputType | 'controller'; source_id?: number;
  hostname: string; http_port?: number | null; username: string; password_configured: boolean; enabled: boolean;
  capabilities: null | { error?: string; route?: string; video_channel_id?: number; playback_verified?: boolean;
    audio_channels?: { id: number; codec: string; supported: boolean; label?: string; video_channel_id?: number }[];
    relay_outputs?: { id: number }[]; doors?: { id: number }[] };
  checked_at?: string;
}
export interface DeviceInput {
  name: string; kind: OutputDevice['kind']; camera_id?: number; hostname: string;
  http_port: number | null; username: string; password: string; enabled: boolean;
}
export interface ActionScenario {
  id?: string; name: string; camera_id: number; enabled: boolean;
  event_type: 'line_crossing' | 'intrusion' | 'motion' | 'face_detected' | 'face_match';
  direction: 'any' | 'entry' | 'exit'; line_id: string; action: 'announce' | 'relay' | 'door';
  device_id: string | null; output_id: number; duration_seconds: number; message: string;
  review_required: boolean; cooldown_seconds: number; max_age_seconds: number; start_hour: number; end_hour: number;
}
export interface ActionJob {
  id: string; kind: string; status: string; device_name: string; event_id?: number; person_id?: number;
  created_at: string; expires_at: string; approved_by?: number;
  intent: { message?: string; person_name?: string; scenario_name?: string; sensor_name?: string; sensor_id?: string; sensor_event_code?: string; sensor_event_value?: unknown; sensor_event_metrics?: Record<string, unknown>; sensor_event_time?: string; approval_reason?: string; channel_id?: number;
    face_confirmation?: 'automatic' | 'reviewer' | 'pending' };
  result?: { message?: string };
}
export interface ActionStatus { schema_ready: boolean; worker_configured: boolean; can_manage: boolean; cloud_control_supported: boolean; worker_online?: boolean; worker_last_seen?: string; }
export interface ManualCommand { kind: 'probe' | 'announce' | 'relay' | 'door'; channel_id?: number; message?: string; volume?: number; voice?: string; duration_seconds?: number; }
export const outputLabels: Record<OutputDevice['kind'], string> = {
  builtin: 'Built-in camera speaker', ip_speaker: 'IP speaker', nvr: 'NVR-connected speaker', controller: 'Gate / relay controller',
};
export const voiceOptions = [{ value: 'en', label: 'English' }, { value: 'en-us', label: 'English (US)' },
  { value: 'en-gb', label: 'English (UK)' }, { value: 'fr', label: 'French' }, { value: 'es', label: 'Spanish' },
  { value: 'hi', label: 'Hindi' }, { value: 'pa', label: 'Punjabi' }];
export const eventActionsService = {
  status: () => apiClient.get<ActionStatus>(`${base}/status`),
  audio: (camera: number) => apiClient.get<AudioSettings>(`${base}/cameras/${camera}/audio`),
  saveAudio: (camera: number, data: AudioSettings) => apiClient.put<AudioSettings>(`${base}/cameras/${camera}/audio`, data),
  devices: (location: number, signal?: AbortSignal) => apiClient.get<{ items: OutputDevice[]; cameras: { camera_id: number; name: string }[] }>(`${base}/locations/${location}/devices`, undefined, { signal }),
  addDevice: (location: number, data: DeviceInput) => apiClient.post<OutputDevice>(`${base}/locations/${location}/devices`, data),
  updateDevice: (id: string, data: DeviceInput) => apiClient.put<OutputDevice>(`${base}/devices/${id}`, data),
  command: (id: string, data: ManualCommand) => apiClient.post<{ id: string; status: string }>(`${base}/devices/${id}/commands`, data),
  scenarios: (location: number, signal?: AbortSignal) => apiClient.get<{ items: ActionScenario[] }>(`${base}/locations/${location}/scenarios`, undefined, { signal }),
  saveScenario: (location: number, data: ActionScenario) => data.id
    ? apiClient.put<ActionScenario>(`${base}/scenarios/${data.id}`, data)
    : apiClient.post<ActionScenario>(`${base}/locations/${location}/scenarios`, data),
  deleteScenario: (id: string) => apiClient.delete(`${base}/scenarios/${id}`),
  jobs: (location: number, signal?: AbortSignal) => apiClient.get<{ items: ActionJob[] }>(`${base}/locations/${location}/jobs`, undefined, { signal }),
  approve: (id: string, approve: boolean) => apiClient.post(`${base}/jobs/${id}/approval`, { approve }),
};

export function actionError(error: any): string {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(item => item.msg).join('. ');
  return error?.message || 'Unable to update event actions';
}
